import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { costsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Upload, Download, Trash2, FileText, FileImage, Loader2, Paperclip } from 'lucide-react';
import { formatDate } from '../lib/utils';

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function FileIcon({ contentType }) {
  if (contentType?.startsWith('image/')) {
    return <FileImage className="h-5 w-5 text-blue-600" />;
  }
  return <FileText className="h-5 w-5 text-red-600" />;
}

export function CostFilesDialog({ cost, open, onOpenChange }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && cost) {
      loadFiles();
    }
  }, [open, cost]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const res = await costsAPI.listFiles(cost.id);
      setFiles(res.data);
    } catch (error) {
      toast.error('Belege konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 10 MB)');
      e.target.value = '';
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await costsAPI.uploadFile(cost.id, formData);
      toast.success('Beleg hochgeladen');
      loadFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await costsAPI.downloadFile(file.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: file.content_type }));
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Datei konnte nicht abgerufen werden');
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Beleg "${file.filename}" wirklich löschen?`)) return;
    try {
      await costsAPI.deleteFile(file.id);
      toast.success('Beleg gelöscht');
      loadFiles();
    } catch (error) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]" data-testid="cost-files-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="h-5 w-5 text-slate-500" />
            Belege zu „{cost?.description}"
          </DialogTitle>
          <DialogDescription>
            Laden Sie Originalbelege als PDF, JPG oder PNG hoch und greifen Sie jederzeit auf sie zu.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="text-sm text-slate-600">
              <p className="font-medium text-slate-900">Beleg hochladen</p>
              <p className="text-xs text-slate-500">PDF, JPG oder PNG · max. 10 MB</p>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              data-testid="cost-file-input"
              className="hidden"
              onChange={handleUpload}
              disabled={uploading}
            />
            <Button
              type="button"
              data-testid="upload-cost-file-button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Hochladen...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Datei wählen
                </>
              )}
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Paperclip className="h-10 w-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">Noch keine Belege hochgeladen</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {files.map((file) => (
                <div
                  key={file.id}
                  data-testid={`cost-file-row-${file.id}`}
                  className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                      <FileIcon contentType={file.content_type} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 truncate">{file.filename}</p>
                      <p className="text-xs text-slate-500">
                        {formatBytes(file.size)} · {formatDate(file.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`download-cost-file-${file.id}`}
                      onClick={() => handleDownload(file)}
                    >
                      <Download className="h-4 w-4 text-slate-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`delete-cost-file-${file.id}`}
                      onClick={() => handleDelete(file)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
